import {
  TUG_BAR_GRADIENT_CSS,
  TUG_BAR_SCALE_TICKS,
  TUG_REFERENCE_NOTE,
  formatTugSeconds,
  getTUGBarProgress,
  getTUGReferenceComparisonNote,
  getTUGScoreColor,
  getTUGSeverityLabel,
  getTUGSeverityRangeLabel,
  getTUGSeverityTone,
  getTugAttemptEntries,
  normalizeTugPayload,
  normalizeTugTime,
  summarizeTugResult,
  type TugAttemptPayload,
  type TugResultPayload,
} from '../tug/tugDomain';

export { getTUGSeverityLabel, getTUGSeverityTone };

interface TUGResultRendererProps {
  payload: TugResultPayload | null | undefined;
}

const AttemptCard = ({
  label,
  attempt,
  highlight = false,
}: {
  label: string;
  attempt: TugAttemptPayload;
  highlight?: boolean;
}) => {
  const timeSeconds = normalizeTugTime(attempt.timeSeconds);
  const severity = getTUGSeverityLabel(timeSeconds, attempt.completed !== false, Boolean(attempt.physicalAssistance));
  const severityTone = getTUGSeverityTone(timeSeconds, attempt.completed !== false, Boolean(attempt.physicalAssistance));
  const rangeLabel = getTUGSeverityRangeLabel(timeSeconds, attempt.completed !== false, Boolean(attempt.physicalAssistance));

  return (
    <div className={`rounded-[24px] border p-5 shadow-sm ${highlight ? 'border-cyan-200 bg-cyan-50/70' : 'border-slate-100 bg-white'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
          <div className="mt-2 text-3xl font-black text-slate-900">
            {attempt.completed !== false && timeSeconds != null ? `${formatTugSeconds(timeSeconds)} s` : 'Não concluiu'}
          </div>
        </div>
        <div className={`rounded-full px-3 py-1.5 text-xs font-bold shadow-sm ${severityTone}`}>{severity}</div>
      </div>

      <div className="mt-4 grid gap-3 text-sm text-slate-600 md:grid-cols-2">
        <div>
          <span className="font-medium text-slate-900">Faixa:</span> {rangeLabel}
        </div>
        <div>
          <span className="font-medium text-slate-900">Auxílio:</span> {attempt.walkingAidLabel}
        </div>
        <div>
          <span className="font-medium text-slate-900">Familiarização:</span> {attempt.familiarizationPerformed ? 'Realizada' : 'Não registrada'}
        </div>
        <div>
          <span className="font-medium text-slate-900">Assistência física:</span> {attempt.physicalAssistance ? 'Sim' : 'Não'}
        </div>
      </div>

      {attempt.reasonNotCompleted ? (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Motivo registrado: {attempt.reasonNotCompleted}
        </div>
      ) : null}

      {attempt.observations ? (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <div className="font-semibold text-slate-900">Observações</div>
          <div className="mt-2 whitespace-pre-wrap">{attempt.observations}</div>
        </div>
      ) : null}
    </div>
  );
};

export default function TUGResultRenderer({ payload }: TUGResultRendererProps) {
  const normalized = normalizeTugPayload(payload);
  const summary = summarizeTugResult(payload);
  const attempts = getTugAttemptEntries(payload);
  const timeSeconds = summary.bestTimeSeconds;
  const scoreColor = getTUGScoreColor(timeSeconds, summary.completed, summary.physicalAssistance, summary.referenceComparison);
  const markerProgress = getTUGBarProgress(timeSeconds);
  const bestAttemptLabel = summary.bestAttempt ? `${summary.bestAttempt}ª tentativa` : '—';
  const activeFlags = summary.clinicalFlags.filter((flag) => flag.active);
  const referenceComparison = summary.referenceComparison;
  const walkingAidMeta =
    summary.walkingAidLabel.toLocaleLowerCase('pt-BR') === 'sem dispositivo'
      ? 'sem dispositivo de marcha'
      : summary.walkingAidLabel.toLocaleLowerCase('pt-BR') === 'sem informação'
        ? 'dispositivo de marcha não informado'
        : `com ${summary.walkingAidLabel.toLocaleLowerCase('pt-BR')}`;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">Timed Up and Go</p>
          <div className="text-6xl font-black leading-none" style={{ color: scoreColor }}>
            {summary.completed && timeSeconds != null ? formatTugSeconds(timeSeconds) : '—'}
          </div>
          <div className="mt-2 text-sm font-semibold text-slate-700">
            {summary.completed && timeSeconds != null ? 'segundos' : 'não concluído'}
          </div>
          <div className="mt-5 inline-flex items-center gap-2 text-sm font-bold" style={{ color: scoreColor }}>
            <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
            {summary.severity}
          </div>
          <div className="mt-3 text-xs font-medium text-slate-500">
            {summary.attemptCount === 2 ? `Melhor de 2 tentativas • ${bestAttemptLabel}` : '1 tentativa registrada'} • {walkingAidMeta}
          </div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{summary.interpretation}</p>
          </div>

          <div className="space-y-2">
            <div className="relative py-1">
              <div
                className="h-9 rounded-full"
                style={{
                  background: TUG_BAR_GRADIENT_CSS,
                }}
              />
              {markerProgress != null && (
                <div
                  className="absolute -top-2 h-[calc(100%+16px)] w-1 rounded-full bg-slate-800"
                  style={{ left: `calc(${markerProgress * 100}% - 2px)` }}
                />
              )}
            </div>
            <div className="relative h-5 text-xs font-medium text-slate-500">
              {TUG_BAR_SCALE_TICKS.map((tick, index) => (
                <span
                  key={tick.label}
                  className="absolute top-0 whitespace-nowrap"
                  style={{
                    left: `${tick.position * 100}%`,
                    transform:
                      index === 0
                        ? 'none'
                        : index === TUG_BAR_SCALE_TICKS.length - 1
                          ? 'translateX(-100%)'
                          : 'translateX(-50%)',
                  }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            Faixa atual: <span className="font-semibold text-slate-900">{summary.severityRange}</span>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Referência prevista</div>
          <div className="mt-2 text-xl font-black text-slate-900">{summary.referenceStatus}</div>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            {getTUGReferenceComparisonNote(referenceComparison, normalized?.patientContext)}
          </p>
        </div>

        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Alertas clínicos</div>
          {activeFlags.length ? (
            <div className="mt-4 space-y-3">
              {activeFlags.map((flag) => (
                <div key={flag.key} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="text-sm font-black text-amber-950">
                    {flag.label} <span className="font-semibold text-amber-800">({flag.thresholdLabel})</span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-amber-900/80">{flag.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Sem alerta pelos pontos de corte de 11,2 s, 12 s ou 13,8 s quando o teste é comparável ao protocolo.
            </p>
          )}
        </div>
      </div>

      {attempts.length ? (
        <div className="space-y-4">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Tentativas registradas</div>
          <div className="grid gap-6 xl:grid-cols-2">
            {attempts.map((entry) => (
              <AttemptCard
                key={`tug-attempt-${entry.index}`}
                label={`${entry.index}ª tentativa${summary.bestAttempt === entry.index ? ' • melhor tempo' : ''}`}
                attempt={entry.attempt}
                highlight={summary.bestAttempt === entry.index}
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Protocolo registrado</div>
          <div className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="flex items-center justify-between gap-4">
              <span>Distância</span>
              <span className="font-semibold text-slate-900">{normalized?.protocol?.distanceMeters ?? 3} m</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Altura da cadeira</span>
              <span className="font-semibold text-slate-900">{normalized?.protocol?.chairHeightCm ?? 46} cm</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Calçado habitual</span>
              <span className="font-semibold text-slate-900">{normalized?.protocol?.regularFootwear === false ? 'Não' : 'Sim'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Assistência física</span>
              <span className="font-semibold text-slate-900">{summary.physicalAssistance ? 'Sim' : 'Não'}</span>
            </div>
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Leitura para doenças respiratórias</div>
          <p className="mt-4 text-sm leading-relaxed text-slate-700">
            {typeof normalized?.referenceNote === 'string' && normalized.referenceNote.trim().length > 0 ? normalized.referenceNote : TUG_REFERENCE_NOTE}
          </p>
          {normalized?.observations ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">Observações registradas</div>
              <div className="mt-2 whitespace-pre-wrap">{normalized.observations}</div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
